import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

const ComparisonContext = createContext();

const comparisonReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_COMPARISON':
      // Don't add if already in comparison (max 4 products)
      if (state.items.find(item => item.id === action.payload.id) || state.items.length >= 4) {
        return state;
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_FROM_COMPARISON':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'CLEAR_COMPARISON':
      return {
        ...state,
        items: []
      };

    case 'LOAD_COMPARISON':
      return {
        ...state,
        items: action.payload
      };

    default:
      return state;
  }
};

export const ComparisonProvider = ({ children }) => {
  const [state, dispatch] = useReducer(comparisonReducer, { items: [] });
  const { currentUser } = useAuth();

  // Load comparison from Firestore when logged in, localStorage when logged out
  useEffect(() => {
    let isMounted = true;

    const loadComparison = async () => {
      if (!currentUser) {
        const savedComparison = localStorage.getItem('comparisonItems');
        if (savedComparison) {
          try {
            dispatch({
              type: 'LOAD_COMPARISON',
              payload: JSON.parse(savedComparison)
            });
          } catch (error) {
            console.error('Error loading comparison:', error);
            localStorage.removeItem('comparisonItems');
          }
        }
        return;
      }

      try {
        let localItems = [];
        const savedComparison = localStorage.getItem('comparisonItems');
        if (savedComparison) {
          try {
            const parsed = JSON.parse(savedComparison);
            if (Array.isArray(parsed)) {
              localItems = parsed;
            }
          } catch {
            localStorage.removeItem('comparisonItems');
          }
        }

        const userRef = doc(db, 'users', currentUser.uid);
        const snapshot = await getDoc(userRef);
        const remoteItems = Array.isArray(snapshot.data()?.comparisonItems) ? snapshot.data().comparisonItems : [];
        const merged = [...remoteItems];
        localItems.forEach((item) => {
          if (item && item.id && !merged.some(existing => existing.id === item.id) && merged.length < 4) {
            merged.push(item);
          }
        });

        if (isMounted) {
          dispatch({ type: 'LOAD_COMPARISON', payload: merged });
        }

        if (localItems.length > 0) {
          localStorage.removeItem('comparisonItems');
        }

        await setDoc(userRef, { comparisonItems: merged }, { merge: true });
      } catch (error) {
        console.error('Error loading comparison:', error);
      }
    };

    loadComparison();
    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  // Save comparison to Firestore when logged in, localStorage when logged out
  useEffect(() => {
    const persistComparison = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          await setDoc(userRef, { comparisonItems: state.items }, { merge: true });
        } catch (error) {
          console.error('Error saving comparison:', error);
        }
      } else {
        localStorage.setItem('comparisonItems', JSON.stringify(state.items));
      }
    };

    persistComparison();
  }, [state.items, currentUser]);

  const addToComparison = (product) => {
    dispatch({ type: 'ADD_TO_COMPARISON', payload: product });
  };

  const removeFromComparison = (productId) => {
    dispatch({ type: 'REMOVE_FROM_COMPARISON', payload: productId });
  };

  const clearComparison = () => {
    dispatch({ type: 'CLEAR_COMPARISON' });
  };

  const getComparisonCount = () => state.items.length;

  const isInComparison = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const canAddToComparison = () => state.items.length < 4;

  return (
    <ComparisonContext.Provider
      value={{
        comparisonItems: state.items,
        addToComparison,
        removeFromComparison,
        clearComparison,
        getComparisonCount,
        isInComparison,
        canAddToComparison
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within ComparisonProvider');
  }
  return context;
};
